/**
 * POS Configuration
 *
 * Config-driven kitchen workflow and order completion behavior.
 * Stored on the restaurant document as `posConfig`.
 * Falls back to concept-based defaults when absent.
 */

// ─── Kitchen Send Trigger ───────────────────────────────────────────────────

/** When unsent items are dispatched to the kitchen relative to payment */
export type KitchenSendTrigger =
  | 'manual'            // Operator presses "Send to Kitchen" explicitly
  | 'onPaymentStart'    // Auto-send when Pay button is tapped (before payment)
  | 'onPaymentComplete' // Auto-send after payment succeeds
  | 'promptOnPay';      // Show "Send to kitchen?" dialog when Pay is tapped

// ─── Order Completion Mode ──────────────────────────────────────────────────

/** How the composite close check evaluates kitchen + payment */
export type OrderCompletionMode =
  | 'closeOnPayment'        // Payment alone closes the order (kitchen irrelevant)
  | 'closeOnServe'          // Order stays open until kitchen serves all items AND paid
  | 'closeOnPaymentOrServe'; // Default: payment OR serve — settled money closes it

// ─── Update Schedule Mode ───────────────────────────────────────────────────

/** When a downloaded OTA update is allowed to install (see IPosUpdateConfig) */
export type UpdateScheduleMode =
  | 'outsideOpenHours' // Only outside operating hours (tied to the restaurant's hours)
  | 'fixedTime';       // Only within a fixed daily window starting at a configured time

// ─── Sub-config Interfaces ──────────────────────────────────────────────────

export interface IPosKitchenConfig {
  /** Whether kitchen workflow is active */
  readonly enabled: boolean;
  /** When to send items to kitchen relative to payment */
  readonly sendTrigger: KitchenSendTrigger;
  /** Show "Send to Kitchen" button for all order types (not just dine-in) */
  readonly showSendButtonAllOrderTypes: boolean;
  /** Allow sending a subset of items to kitchen */
  readonly allowPartialSend: boolean;
  /** Counter handoff: auto-mark all items served when payment completes */
  readonly autoServeOnPayment: boolean;
}

export interface IPosOrderCompletionConfig {
  /** How kitchen + payment interact for closing */
  readonly mode: OrderCompletionMode;
  /** Automatically start a new order after closing */
  readonly autoStartNewOrder: boolean;
  /** Delay in ms before auto-starting new order */
  readonly newOrderDelayMs: number;
}

/**
 * The tip quick picks a restaurant gets until it saves its own: 15 / 18 / 20.
 *
 * The POS falls back to exactly this set (`kDefaultTipPercentages` in
 * `apps/pos/lib/utils/payments/tip_suggestions.dart`) and prints it on the card
 * slip, so the management editor and every seeded config read it from here.
 * Changing one copy without the other shows an operator presets the till never
 * offers.
 */
export const DEFAULT_TIP_PERCENTAGES: readonly number[] = [0.15, 0.18, 0.2];

export interface IPosPaymentConfig {
  /** Whether tips are enabled on POS */
  readonly allowTips: boolean;
  /** Tip percentage presets shown on the tip screen, as fractions (default {@link DEFAULT_TIP_PERCENTAGES}) */
  readonly tipPercentages: readonly number[];
  /** Auto-print receipt after card payments */
  readonly autoPrintCardReceipts: boolean;
  /** Auto-print receipt after cash payments */
  readonly autoPrintCashReceipts: boolean;
  /** Print customer receipt copy */
  readonly printCustomerReceipt: boolean;
  /** Print merchant receipt copy */
  readonly printMerchantReceipt: boolean;
  /** Open cash drawer on cash payments */
  readonly openCashDrawerOnCash: boolean;
  /** Open cash drawer on card payments */
  readonly openCashDrawerOnCard: boolean;
  /**
   * Restaurant-wide DEFAULT drawer pulse width in ms (100–500). A drawer's own
   * `ICashDrawer.pulseMs` wins; this fills it in when blank, which is the
   * common case since the per-drawer field is optional.
   *
   * There is deliberately NO matching `cashDrawerPin`. A pin says WHICH
   * physical connector, and `ICashDrawer.pin` is REQUIRED — so a restaurant-wide
   * pin was a fallback that could never be consulted, while still appearing in
   * the back office as a setting that did something. A pulse width is a tuning
   * value with a genuine default, which is why this one is real.
   */
  readonly cashDrawerPulseMs: number;
  /**
   * Absolute over/short (in CENTS) a drawer close may reach before it requires
   * a manager's approval. 0 means every non-zero variance needs approval.
   *
   * Restaurant-configurable on purpose: the right threshold is a function of
   * average ticket and cash mix, so a hardcoded value is either theatre in a
   * high-volume store or a constant interruption in a low-volume one.
   */
  readonly varianceApprovalThresholdCents: number;
  /**
   * Every shift starts on a COUNTED drawer: the till opens the drawer and asks
   * for a denomination count sheet, never a pre-filled number to accept, and
   * never opens a shift silently. The SERVER refuses an open without the sheet
   * (`CASH_OPENING_COUNT_REQUIRED`), so an older till cannot skip it.
   *
   * Absent means `false` — the per-drawer behaviour (a shared `automatic`
   * drawer opens on its configured float with no count).
   */
  readonly requireOpeningCount?: boolean;
  /**
   * No cash tender until a shift is open on this till's drawer. Enforced by the
   * till BEFORE any cash changes hands — never by the server refusing the
   * record afterwards: a record refused after the money is in the drawer loses
   * the record, not the money. A till with no drawer configured is not blocked.
   *
   * Absent means `false`.
   */
  readonly requireCashShift?: boolean;
}

/**
 * How the gateway(s) at a location establish authority to issue orderNumber
 * lease blocks. See `IPosConfig.coordinatorMode`.
 */
export type GatewayCoordinatorMode = 'solo' | 'elected';

export interface IPosUpdateConfig {
  /** When a downloaded OTA update may install */
  readonly scheduleMode: UpdateScheduleMode;
  /** Start of the install window for 'fixedTime', "HH:mm" (24h, local) */
  readonly fixedTime?: string;
  /** How long the fixed-time window stays open, in minutes (default 120) */
  readonly fixedWindowMinutes?: number;
  /**
   * Allow installing during the schedule's "busy" period when the tablet is
   * idle AND not busy — a seamless mid-shift lull update. Only acts on managed
   * (device-owner) terminals, where the install is silent and auto-relaunches.
   */
  readonly applyWhenIdle?: boolean;
  /** Minutes with no user activity before the tablet counts as idle (default 5) */
  readonly idleThresholdMinutes?: number;
  /**
   * Minutes a terminal must sit with NO employee session before it counts as
   * idle (default 60). A logged-out terminal is NOT idle by default — every
   * cold boot lands at the PIN screen, and mid-shift staff tap in at any
   * moment; treating "no session" as "idle" let five releases hard-restart
   * terminals throughout a business day (2026-07-11 incident).
   */
  readonly sessionlessIdleFloorMinutes?: number;
  /**
   * Minimum minutes between automatic apply-restarts outside the configured
   * install window (default 240). In-window installs and forced (critical)
   * pushes are never limited.
   */
  readonly minAutoRestartGapMinutes?: number;
}

// ─── Top-Level POS Config ───────────────────────────────────────────────────

// ─── Shift Review (the "X tape" an employee reads before clocking out) ──────

/**
 * One section of the shift-review report.
 *
 * The vocabulary is deliberately a CLOSED UNION rather than free strings: the
 * POS renders each section with its own code, so a key it does not recognise
 * can only be dropped silently. A closed union makes an unknown section a
 * compile error on the terminal instead.
 */
export type ShiftReviewSectionKey =
  | 'employeeAccount'
  | 'tipsAndFeesEarned'
  | 'myGuestReport'
  | 'creditTipAudit'
  | 'tipSharing'
  | 'delivery'
  | 'salesAndTaxesSummary'
  | 'cashCreditPerSalesCategory'
  | 'revenueCenters'
  | 'totalVoids'
  | 'totalRemovals'
  | 'totalDiscounts'
  | 'totalPayments'
  | 'creditCardBreakdown'
  | 'otherBreakdown'
  | 'employeeSignature'
  | 'payOuts';

/**
 * Canonical order and default visibility, used when a restaurant has never
 * configured the report AND to place any section added by a later release.
 *
 * ORDER HERE IS THE DEFAULT PRINT ORDER. It follows the sequence an operator
 * reads: what you owe, what you earned, who you served, then the breakdowns,
 * then the signature.
 */
export const SHIFT_REVIEW_SECTION_ORDER: readonly ShiftReviewSectionKey[] = [
  'employeeAccount',
  'tipsAndFeesEarned',
  'myGuestReport',
  'creditTipAudit',
  'tipSharing',
  'delivery',
  'salesAndTaxesSummary',
  'cashCreditPerSalesCategory',
  'revenueCenters',
  'totalVoids',
  'totalRemovals',
  'totalDiscounts',
  'totalPayments',
  'creditCardBreakdown',
  'otherBreakdown',
  'employeeSignature',
  'payOuts',
];

/**
 * The sections the TILL can actually draw today.
 *
 * DART TWIN: `kShiftReviewSectionsOnTill` in
 * `apps/pos/lib/models/config/shift_review_config.dart`, pinned by that repo's
 * `shift_review_config_test.dart`.
 *
 * WHY IT IS PUBLISHED TO THE BACK OFFICE: every section below is offered in
 * Management, and one the till cannot draw would simply not appear on the
 * report — leaving the operator who ticked it unable to tell a hidden section
 * from an unbuilt one. The settings screen says so beside each section instead.
 *
 * Move a key into this list IN THE SAME CHANGE that teaches the till to draw
 * it, never ahead of it.
 */
export const SHIFT_REVIEW_SECTIONS_ON_TILL: ReadonlySet<ShiftReviewSectionKey> =
  new Set<ShiftReviewSectionKey>([
    'employeeAccount',
    'tipsAndFeesEarned',
    'myGuestReport',
    'creditTipAudit',
    'tipSharing',
    'delivery',
    'salesAndTaxesSummary',
    'cashCreditPerSalesCategory',
    'revenueCenters',
    'totalVoids',
    'totalRemovals',
    'totalDiscounts',
    'totalPayments',
    'creditCardBreakdown',
    'otherBreakdown',
    'employeeSignature',
    'payOuts',
  ]);

export interface IShiftReviewSection {
  readonly key: ShiftReviewSectionKey;
  readonly visible: boolean;
}

/**
 * Which driver the till uses to reach a NETWORK printer.
 *
 * `epsonSdk` is the proven fleet default. `rawEscPos` sends byte-for-byte what
 * the gateway sends over TCP:9100, which is the only way a non-Epson network
 * printer works on a till. The two local buses (USB, Bluetooth) always use the
 * SDK regardless — a raw socket cannot reach either.
 */
export type PosNetworkPrinterDriver = 'epsonSdk' | 'rawEscPos';

/**
 * Whether a cash drawer reads OPEN when its sense line is high.
 *
 * HIGH is the wiring of essentially every Epson-compatible drawer and is the
 * gateway's default, so only an explicit `low` inverts it: the loudest failure
 * drawer reporting can produce is telling a store that every shut till is open.
 */
export type PosDrawerOpenSignal = 'high' | 'low';

/**
 * Per-restaurant printing behaviour for the TILL's own printer plane.
 *
 * 🔴 WHY THIS EXISTS. `apps/pos` has read `posConfig.printing.networkDriver` and
 * `posConfig.printing.drawerOpenSignal` since the till gained a raw ESC/POS
 * transport — and nothing in this repo declared them. No model field, no
 * schema, no management UI, so the only way to set either was to hand-edit a
 * Firestore document. A reader with no writer is the same defect class the
 * order-completion mode carried below; this closes it.
 *
 * Both fields are OPTIONAL with no schema default, deliberately. The till
 * already applies the safe fallback for each (`NetworkPrinterDriver.fromWire`
 * keeps the SDK on anything unrecognised, and only the literal `low` inverts
 * the drawer), and defaulting here would stamp an opinion onto every posConfig
 * save for restaurants that never expressed one.
 */
export interface IPosPrintingConfig {
  readonly networkDriver?: PosNetworkPrinterDriver;
  readonly drawerOpenSignal?: PosDrawerOpenSignal;
}

export interface IPosShiftReviewConfig {
  /**
   * Block clock-out until the employee has completed their shift review.
   *
   * DEFAULT FALSE, and that is not timidity — it is the only safe default.
   * Defaulting true would, on the next deploy, stop every server in every
   * existing restaurant from clocking out until they cleared a report nobody
   * told them about. A back-office setting that changes behaviour on estate
   * that never opted in is an outage, not a feature.
   *
   * It binds ONLY an employee who has order records on the shift — see
   * `shiftReviewIsRequiredFor`. Someone who rang nothing must never be trapped
   * at the terminal by a report with no content.
   */
  readonly requiredBeforeClockOut: boolean;
  /**
   * The sections, IN PRINT ORDER. The array index is the order; there is
   * deliberately no separate `position` field, because two representations of
   * one ordering is how the stored order and the rendered order come to
   * disagree with nothing on screen looking wrong.
   *
   * A stored config need not be exhaustive — see `resolveShiftReviewSections`.
   */
  readonly sections: readonly IShiftReviewSection[];
}

/**
 * The sections to render, merging a restaurant's stored config with the
 * canonical list.
 *
 * WHY THIS EXISTS: a restaurant that saved its report before a new section
 * shipped has a stored array that does not mention it. Rendering the stored
 * array verbatim would make every section added after that save INVISIBLE to
 * that restaurant, permanently and silently — they would simply never see the
 * new content and would have no way to know it existed.
 *
 * So: stored entries keep their order and their `visible` flag, and anything
 * missing is APPENDED in canonical order, visible. New content shows up by
 * default; an operator's explicit decision to hide something is never
 * overridden. Unknown keys in stored config are dropped — they are sections
 * this build cannot render.
 */
export function resolveShiftReviewSections(
  stored: readonly IShiftReviewSection[] | undefined,
): readonly IShiftReviewSection[] {
  const known = new Set<string>(SHIFT_REVIEW_SECTION_ORDER);
  const seen = new Set<ShiftReviewSectionKey>();
  const resolved: IShiftReviewSection[] = [];

  for (const section of stored ?? []) {
    if (!known.has(section.key) || seen.has(section.key)) continue;
    seen.add(section.key);
    resolved.push({ key: section.key, visible: section.visible });
  }
  for (const key of SHIFT_REVIEW_SECTION_ORDER) {
    if (!seen.has(key)) resolved.push({ key, visible: true });
  }
  return resolved;
}

/**
 * Does this employee have to complete a shift review before clocking out?
 *
 * TWO conditions, and the second is the operator's own words: *"anyone with
 * records of any order is required to complete a checkout report"*. An
 * employee who rang nothing has no report to complete, so gating them would
 * strand a host or a busser at the terminal with an empty tape and no way past
 * it.
 */
export function shiftReviewIsRequiredFor(
  config: IPosShiftReviewConfig | undefined,
  employeeOrderCount: number,
): boolean {
  if (!config?.requiredBeforeClockOut) return false;
  return employeeOrderCount > 0;
}

export interface IPosConfig {
  readonly kitchen: IPosKitchenConfig;
  readonly orderCompletion: IPosOrderCompletionConfig;
  readonly payment: IPosPaymentConfig;
  /** The X-tape an employee reads before clocking out, and whether it is required */
  readonly shiftReview?: IPosShiftReviewConfig;

  /** Per-restaurant printing behaviour for the till — see IPosPrintingConfig. */
  readonly printing?: IPosPrintingConfig;
  /** When/how OTA updates install on the terminal */
  readonly update?: IPosUpdateConfig;
  /** Idle timeout in seconds before auto-logout (30–900, increments of 30) */
  readonly idleTimeoutSeconds?: number;
  /**
   * Enable the on-premises gateway's T2/T3 offline-payment relay for this
   * restaurant. Relayed to the (Firestore-less) gateway, which starts/stops its
   * relay watcher accordingly. Default false — opt in per restaurant.
   */
  readonly paymentRelayEnabled?: boolean;
  /**
   * Enable the on-premises gateway's LAN/direct order-awareness delivery for
   * this restaurant. Relayed to the (Firestore-less) gateway, which starts/stops
   * its order_awareness_queue watcher accordingly (cloud/FCM awareness is
   * unaffected — it always runs). Default false — opt in per restaurant.
   */
  readonly awarenessRelayEnabled?: boolean;
  /**
   * Lease-authority mode for the on-premises gateway(s) at this LOCATION.
   *
   *   'solo'    — one gateway, always primary (the default).
   *   'elected' — two or more gateways, each claiming an RTDB coordinator
   *               lease so exactly one issues orderNumber blocks at a time.
   *
   * Delivered from here because it is a property of the SITE, not of a box. It
   * used to be a per-device gateway dashboard setting defaulting to 'solo' —
   * and 'solo' means "I am always primary", so installing a second gateway
   * produced TWO always-primary coordinators unless someone remembered to
   * change it on both boxes, one at a time, with nothing checking. Two
   * always-primary coordinators can issue overlapping order numbers.
   *
   * Undefined means "do not change it": the gateway keeps its own setting, so
   * a restaurant that has never set this cannot be silently reset to solo.
   */
  readonly coordinatorMode?: GatewayCoordinatorMode;
  /**
   * When EXPLICITLY true, the POS signals to the card processor that it
   * accepts partial approvals. A partially-approved card
   * (respcode 10, approved < requested) is kept as a real payment at the
   * approved amount, and the operator collects the remainder another way.
   *
   * When false OR ABSENT, the processor auto-reverses the partial and nothing
   * is taken.
   *
   * Absent means OFF (owner decision 2026-10-14): a location opts in by saving
   * `true`. `isPartialApprovalAccepted(posConfig)` is the one reader.
   */
  readonly acceptPartialApprovals?: boolean;
}

// ─── Concept-Based Defaults ─────────────────────────────────────────────────

const RESTAURANT_DEFAULTS: IPosConfig = {
  kitchen: {
    enabled: true,
    sendTrigger: 'manual',
    showSendButtonAllOrderTypes: false,
    allowPartialSend: true,
    autoServeOnPayment: false,
  },
  orderCompletion: {
    mode: 'closeOnPaymentOrServe',
    autoStartNewOrder: false,
    newOrderDelayMs: 0,
  },
  payment: {
    allowTips: true,
    tipPercentages: DEFAULT_TIP_PERCENTAGES,
    autoPrintCardReceipts: false,
    autoPrintCashReceipts: true,
    printCustomerReceipt: true,
    printMerchantReceipt: true,
    openCashDrawerOnCash: true,
    openCashDrawerOnCard: false,
    cashDrawerPulseMs: 100,
    varianceApprovalThresholdCents: 500,
  },
  idleTimeoutSeconds: 60,
  paymentRelayEnabled: false,
  awarenessRelayEnabled: false,
};

const QUICK_SERVICE_DEFAULTS: IPosConfig = {
  kitchen: {
    enabled: true,
    sendTrigger: 'onPaymentComplete',
    showSendButtonAllOrderTypes: true,
    allowPartialSend: false,
    autoServeOnPayment: true,
  },
  orderCompletion: {
    mode: 'closeOnPayment',
    autoStartNewOrder: true,
    newOrderDelayMs: 500,
  },
  payment: {
    allowTips: true,
    tipPercentages: DEFAULT_TIP_PERCENTAGES,
    autoPrintCardReceipts: true,
    autoPrintCashReceipts: true,
    printCustomerReceipt: true,
    printMerchantReceipt: true,
    openCashDrawerOnCash: true,
    openCashDrawerOnCard: false,
    cashDrawerPulseMs: 100,
    varianceApprovalThresholdCents: 500,
  },
  idleTimeoutSeconds: 60,
  paymentRelayEnabled: false,
  awarenessRelayEnabled: false,
};

const RETAIL_DEFAULTS: IPosConfig = {
  kitchen: {
    enabled: false,
    sendTrigger: 'manual',
    showSendButtonAllOrderTypes: false,
    allowPartialSend: false,
    autoServeOnPayment: false,
  },
  orderCompletion: {
    mode: 'closeOnPayment',
    autoStartNewOrder: true,
    newOrderDelayMs: 500,
  },
  payment: {
    allowTips: false,
    tipPercentages: DEFAULT_TIP_PERCENTAGES,
    autoPrintCardReceipts: true,
    autoPrintCashReceipts: true,
    printCustomerReceipt: true,
    printMerchantReceipt: true,
    openCashDrawerOnCash: true,
    openCashDrawerOnCard: false,
    cashDrawerPulseMs: 100,
    varianceApprovalThresholdCents: 500,
  },
  idleTimeoutSeconds: 60,
  paymentRelayEnabled: false,
  awarenessRelayEnabled: false,
};

/**
 * Get default POS config based on restaurant concept.
 * Used as fallback when no explicit posConfig is stored.
 */
export function getDefaultPosConfig(concept: string): IPosConfig {
  const normalized = concept.toLowerCase().trim();

  if (normalized.includes('quick') || normalized.includes('fast') || normalized === 'quick_service') {
    return QUICK_SERVICE_DEFAULTS;
  }
  if (normalized.includes('retail') || normalized.includes('shop') || normalized.includes('store')) {
    return RETAIL_DEFAULTS;
  }
  // Default: full-service restaurant
  return RESTAURANT_DEFAULTS;
}

/**
 * Merge a partial posConfig from Firestore over concept-based defaults.
 * Ensures every field has a value even if only a subset was saved.
 */
export function buildPosConfig(
  partial: Partial<IPosConfig> | undefined,
  concept: string,
): IPosConfig {
  const defaults = getDefaultPosConfig(concept);
  if (!partial) return defaults;

  return {
    kitchen: {
      ...defaults.kitchen,
      ...(partial.kitchen ?? {}),
    },
    orderCompletion: {
      ...defaults.orderCompletion,
      ...(partial.orderCompletion ?? {}),
    },
    payment: {
      ...defaults.payment,
      ...(partial.payment ?? {}),
    },
    idleTimeoutSeconds: partial.idleTimeoutSeconds ?? defaults.idleTimeoutSeconds,
    paymentRelayEnabled: partial.paymentRelayEnabled ?? defaults.paymentRelayEnabled,
    awarenessRelayEnabled: partial.awarenessRelayEnabled ?? defaults.awarenessRelayEnabled,
    // coordinatorMode is passed through as-saved and is NOT defaulted here.
    // Absent means "the cloud is not expressing an opinion", which the gateway
    // reads as "keep what you are running". Defaulting it to 'solo' would send
    // that to every restaurant and could demote a deliberately-elected
    // multi-gateway site into two always-primary coordinators.
    ...(partial.coordinatorMode !== undefined && {
      coordinatorMode: partial.coordinatorMode,
    }),
    // Preserve the OTA update policy. `update` is optional (older restaurants
    // predate it) and has no concept default, so it is passed through as-saved —
    // callers (management form, POS) apply per-field fallbacks. Omitting this is
    // what silently dropped the configured update window on every model read.
    ...(partial.update !== undefined && { update: partial.update }),
    // Preserve the shift review ("X tape") config the same way, for the same
    // reason: it is optional and resolved per-field by its readers
    // (`resolveShiftReviewSections`, `shiftReviewIsRequiredFor`). Dropping it here
    // hid the saved setting from management's POS page, whose next Save of the
    // whole posConfig then erased it.
    ...(partial.shiftReview !== undefined && { shiftReview: partial.shiftReview }),
    // Same passthrough, same reason: optional, no concept default, and resolved
    // per-field by the till. Dropping it here would hide the operator's choice
    // from management's POS page, whose next Save of the whole posConfig would
    // then erase it.
    ...(partial.printing !== undefined && { printing: partial.printing }),
    // acceptPartialApprovals: optional boolean, absent means OFF. Pass through
    // as-saved; do NOT default it here — writing a value for an absent field
    // would turn an unset location into an explicit choice nobody made.
    ...(partial.acceptPartialApprovals !== undefined && {
      acceptPartialApprovals: partial.acceptPartialApprovals,
    }),
  };
}

/**
 * Returns true when the location accepts partial card approvals.
 *
 * Only an explicit `true` turns it on (owner decision 2026-10-14). Absent
 * (`undefined`) or `false` is OFF: the processor auto-reverses a partial and
 * nothing is taken, so the operator collects the whole bill another way.
 */
export function isPartialApprovalAccepted(
  posConfig: Pick<IPosConfig, 'acceptPartialApprovals'>,
): boolean {
  return posConfig.acceptPartialApprovals === true;
}

// ─── Reading the completion mode off a restaurant ───────────────────────────

/** Every legal `OrderCompletionMode`, for validating what a document holds. */
export const ORDER_COMPLETION_MODES: readonly OrderCompletionMode[] = [
  'closeOnPayment',
  'closeOnServe',
  'closeOnPaymentOrServe',
] as const;

/**
 * `posConfig.orderCompletion.mode` off a restaurant document, defaulted.
 *
 * 🔴 THE SECOND ORPHAN. `IPosOrderCompletionConfig.mode` has always had three
 * values and a management UI behind it, and all three services that can close
 * an order — `checkService`, `orderLifecycleService`, `kitchenStateService` —
 * passed the LITERAL `'closeOnPaymentOrServe'` to `canCloseWithChecks`. The
 * setting configured nothing: a restaurant that chose `closeOnServe` got
 * `closeOnPaymentOrServe`, and one that chose `closeOnPayment` got it too.
 *
 * That mattered little while `closeOnPaymentOrServe` behaved as `payment AND
 * serve`, because the strictest mode was the one everybody silently got. It
 * matters now: the default closes on payment, so `closeOnServe` is the only way
 * to ask for the kitchen gate — and an unread setting is not a way.
 *
 * An unrecognised string falls back to the default rather than throwing or
 * disabling the close. A typo in one restaurant's document must not be the
 * reason its tables stop closing; that is the failure this whole change exists
 * to remove.
 */
export function readOrderCompletionMode(
  locationRecord:
    | { posConfig?: { orderCompletion?: { mode?: string } } }
    | null
    | undefined,
): OrderCompletionMode {
  const mode = locationRecord?.posConfig?.orderCompletion?.mode;
  return ORDER_COMPLETION_MODES.includes(mode as OrderCompletionMode)
    ? (mode as OrderCompletionMode)
    : 'closeOnPaymentOrServe';
}
